import { movementParts, parseJson } from './careLogSummary'

/**
 * Goal typing and progress metrics for the Goals → Progress tab.
 *
 * A goal's *type* decides which number actually tells you whether it is going
 * well: a "prevent falls" goal is measured by incidents, a "maintain memory"
 * goal by its domain score, an "improve hydration" goal by a daily average.
 * Typing is keyword-based because goals are free text with no structure.
 */

export const GOAL_TYPES = {
  prevent: { label: 'Prevent', className: 'badge-danger' },
  maintain: { label: 'Maintain', className: 'badge-blue' },
  improve: { label: 'Improve', className: 'badge-green' },
  ongoing: { label: 'Ongoing', className: 'badge-grey' },
}

// Checked in order — the first list that matches wins.
const TYPE_RULES = [
  ['prevent', ['prevent', 'avoid', 'reduce', 'fall', 'hospital']],
  ['maintain', ['maintain', 'stable', 'keep', 'memory', 'function', 'independence']],
  ['improve', ['improve', 'increase', 'better', 'more', 'build']],
]

export function detectGoalType(goal) {
  const hay = `${goal?.title || ''} ${goal?.description || ''}`.toLowerCase()
  for (const [type, words] of TYPE_RULES) {
    if (words.some((w) => hay.includes(w))) return type
  }
  return 'ongoing'
}

/** Cached type wins when present, so a human correction survives a re-render. */
export function goalTypeFor(goal, cached) {
  const stored = cached && typeof cached === 'object' ? cached[goal.title] : null
  return GOAL_TYPES[stored] ? stored : detectGoalType(goal)
}

export function buildGoalTypes(goals) {
  return Object.fromEntries(goals.map((g) => [g.title, detectGoalType(g)]))
}

/* ── Domain matching (Maintain goals) ─────────────────────────── */

const DOMAIN_RULES = [
  ['domain_memory', ['memory', 'cognit', 'confus', 'dementia'], 'Memory'],
  ['domain_mobility', ['mobility', 'fall', 'walk', 'balance', 'strength'], 'Mobility'],
  ['domain_mood', ['mood', 'depress', 'anxious', 'wellbeing'], 'Mood'],
  ['domain_sleep', ['sleep', 'night', 'rest'], 'Sleep'],
  ['domain_appetite', ['appetite', 'eat', 'meal', 'weight', 'nutrition'], 'Appetite'],
  ['domain_behaviour', ['behaviour', 'behavior', 'agitat', 'wander'], 'Behaviour'],
  ['domain_pain', ['pain', 'ache', 'discomfort'], 'Pain'],
  ['domain_communication', ['communicat', 'speech', 'hearing'], 'Communication'],
  ['domain_personal_care', ['hygiene', 'personal care', 'dress', 'bath'], 'Personal care'],
  ['domain_social_engagement', ['social', 'lonely', 'isolat', 'friend', 'visit'], 'Social'],
]

export function domainForGoal(goal) {
  const hay = `${goal?.title || ''} ${goal?.description || ''}`.toLowerCase()
  for (const [key, words, label] of DOMAIN_RULES) {
    if (words.some((w) => hay.includes(w))) return { key, label }
  }
  return null
}

/* ── Weekly bucketing ─────────────────────────────────────────── */

const DAY_MS = 86400000

/** Four consecutive 7-day windows ending today, oldest first. */
export function weekWindows(now = new Date()) {
  const end = new Date(now)
  end.setHours(23, 59, 59, 999)
  return Array.from({ length: 4 }, (_, i) => {
    const to = new Date(end.getTime() - (3 - i) * 7 * DAY_MS)
    const from = new Date(to.getTime() - 6 * DAY_MS)
    from.setHours(0, 0, 0, 0)
    return { from, to }
  })
}

const inWeek = (iso, w) => {
  if (!iso) return false
  const d = new Date(`${iso}T12:00:00`)
  return d >= w.from && d <= w.to
}

const avg = (nums) => (nums.length ? nums.reduce((a, b) => a + b, 0) / nums.length : null)

/* ── The metric that actually measures each goal ──────────────── */

/**
 * Returns { label, unit, weekly: [n|null x4], headline, betterIsUp }.
 * `weekly` is null for a week with no data — the bar renders empty rather
 * than as a misleading zero.
 */
export function goalMetric({ type, goal, logs = [], incidents = [], progress = [] }) {
  const weeks = weekWindows()

  if (type === 'prevent') {
    const weekly = weeks.map((w) => incidents.filter((i) => inWeek(i.incident_date, w)).length)
    const thisMonth = incidents.length
    return {
      label: 'Incidents', unit: '', betterIsUp: false, weekly,
      headline: `${thisMonth} this month`,
      headlineValue: thisMonth,
    }
  }

  if (type === 'maintain') {
    const domain = domainForGoal(goal)
    const latest = progress[0]
    const prior = progress[1]
    const score = domain && latest ? latest[domain.key] : null
    const priorScore = domain && prior ? prior[domain.key] : null
    // Domain scores are monthly, so the weekly bars use average mood — the
    // closest thing collected daily. Labelled so it is never read as the
    // domain score itself.
    const weekly = weeks.map((w) => {
      const vals = logs
        .filter((l) => inWeek(l.log_date, w))
        .flatMap((l) => [l.mood_morning, l.mood_afternoon, l.mood_evening])
        .filter((v) => v != null)
      const a = avg(vals)
      return a == null ? null : Math.round(a * 10) / 10
    })
    return {
      label: domain ? `${domain.label} score` : 'Domain score',
      unit: '/5', betterIsUp: true, weekly, weeklyLabel: 'avg mood',
      headline: score != null ? `${score}/5` : 'Not assessed',
      headlineValue: score,
      priorValue: priorScore,
    }
  }

  if (type === 'improve') {
    const hay = `${goal?.title || ''} ${goal?.description || ''}`.toLowerCase()

    if (/fluid|hydrat|water|drink/.test(hay)) {
      const weekly = weeks.map((w) => {
        const vals = logs.filter((l) => inWeek(l.log_date, w) && l.fluids_ml).map((l) => l.fluids_ml)
        const a = avg(vals)
        return a == null ? null : Math.round(a)
      })
      return { label: 'Avg daily fluids', unit: 'ml', betterIsUp: true, weekly, headline: fmtLast(weekly, 'ml') }
    }

    if (/social|lonely|isolat|friend|visit|engage/.test(hay)) {
      const weekly = weeks.map((w) => {
        const days = logs.filter((l) => inWeek(l.log_date, w))
        if (!days.length) return null
        return days.filter((l) => l.task_activities === true).length
      })
      return { label: 'Days with activities', unit: '/wk', betterIsUp: true, weekly, headline: fmtLast(weekly, ' days') }
    }

    // Default improve metric: movement minutes, the most commonly tracked.
    const weekly = weeks.map((w) => {
      const vals = logs
        .filter((l) => inWeek(l.log_date, w))
        .map((l) => movementParts(l.movement_json)?.duration)
        .filter((v) => v != null)
      const a = avg(vals)
      return a == null ? null : Math.round(a)
    })
    return { label: 'Avg movement', unit: 'min', betterIsUp: true, weekly, headline: fmtLast(weekly, ' min') }
  }

  // Ongoing — no single number defines success; show logging activity.
  const weekly = weeks.map((w) => logs.filter((l) => inWeek(l.log_date, w)).length)
  return { label: 'Care logs', unit: '/wk', betterIsUp: true, weekly, headline: fmtLast(weekly, ' logs') }
}

function fmtLast(weekly, suffix) {
  const last = [...weekly].reverse().find((v) => v != null)
  return last == null ? 'No data yet' : `${last}${suffix}`
}

/* ── Status ───────────────────────────────────────────────────── */

export const STATUS = {
  on_track: { label: 'On track', className: 'badge-green' },
  attention: { label: 'Needs attention', className: 'badge-amber' },
  no_data: { label: 'No data yet', className: 'badge-grey' },
}

export function goalStatus(type, metric) {
  const known = metric.weekly.filter((v) => v != null)

  if (type === 'prevent') {
    if (metric.headlineValue == null) return 'no_data'
    return metric.headlineValue === 0 ? 'on_track' : 'attention'
  }

  if (type === 'maintain') {
    if (metric.headlineValue == null) return 'no_data'
    const declining = metric.priorValue != null && metric.headlineValue < metric.priorValue
    if (declining) return 'attention'
    return metric.headlineValue >= 3 ? 'on_track' : 'attention'
  }

  // improve / ongoing — need at least two weeks to call a direction.
  if (known.length < 2) return 'no_data'
  const first = known[0]
  const last = known[known.length - 1]
  return last > first ? 'on_track' : 'attention'
}

/* ── Month-over-month summary ─────────────────────────────────── */

export const TRAJECTORY = {
  stable: { label: 'Stable', className: 'badge-blue' },
  improvement: { label: 'Improving', className: 'badge-green' },
  gradual_decline: { label: 'Declining', className: 'badge-amber' },
  acute_change: { label: 'Acute change', className: 'badge-danger' },
}

/** The three domains that moved most between the two most recent months. */
export function topDomainDeltas(progress = [], limit = 3) {
  const [latest, prior] = progress
  if (!latest || !prior) return []
  return DOMAIN_RULES
    .map(([key, , label]) => ({ key, label, now: latest[key], was: prior[key] }))
    .filter((d) => d.now != null && d.was != null)
    .map((d) => ({ ...d, delta: d.now - d.was }))
    .sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta))
    .slice(0, limit)
}

export { parseJson }
